import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { Search, MapPin, Plus, X, Navigation } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/mileage`;

const TripCreate = () => {
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [stops, setStops] = useState([]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!origin || !destination) {
      toast.error('Please select both origin and destination');
      return;
    }

    setLoading(true);
    try {
      await api.post(`${API}/trips`, {
        date,
        origin: {
          name: origin.name,
          address: origin.address,
          lat: origin.lat,
          lon: origin.lon,
        },
        destination: {
          name: destination.name,
          address: destination.address,
          lat: destination.lat,
          lon: destination.lon,
        },
        stops: stops.map((stop) => ({
          name: stop.name,
          address: stop.address,
          lat: stop.lat,
          lon: stop.lon,
        })),
        notes,
      });

      toast.success('Trip created successfully!');
      navigate('/trips');
    } catch (error) {
      console.error('Error creating trip:', error);
      toast.error('Failed to create trip');
    } finally {
      setLoading(false);
    }
  };

  const addStop = () => {
    setStops([...stops, null]);
  };

  const removeStop = (index) => {
    setStops(stops.filter((_, i) => i !== index));
  };

  const updateStop = (index, location) => {
    const newStops = [...stops];
    newStops[index] = location;
    setStops(newStops);
  };

  return (
    <div className="space-y-4 md:space-y-6 fade-in">
      <form onSubmit={handleSubmit} className="glass-card p-4 md:p-6 space-y-4 md:space-y-6">
        {/* Date */}
        <div>
          <label className="block text-sm font-medium mb-2">Trip Date</label>
          <input
            type="date"
            data-testid="trip-date-input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="glass-input w-full text-sm md:text-base"
            required
          />
        </div>

        {/* Origin */}
        <div>
          <label className="block text-sm font-medium mb-2">
            <MapPin className="w-4 h-4 inline mr-1 text-[#14B8A6]" />
            Origin
          </label>
          <LocationSearch
            selectedLocation={origin}
            onSelect={setOrigin}
            placeholder="Search for origin location..."
            testId="origin"
          />
        </div>

        {/* Stops */}
        {stops.map((stop, index) => (
          <div key={index}>
            <label className="block text-sm font-medium mb-2 flex items-center justify-between">
              <span>
                <MapPin className="w-4 h-4 inline mr-1 text-[#06B6D4]" />
                Stop {index + 1}
              </span>
              <button
                type="button"
                onClick={() => removeStop(index)}
                data-testid={`remove-stop-${index}`}
                className="text-red-400 hover:text-red-300 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </label>
            <LocationSearch
              selectedLocation={stop}
              onSelect={(loc) => updateStop(index, loc)}
              placeholder={`Search for stop ${index + 1}...`}
              testId={`stop-${index}`}
            />
          </div>
        ))}

        {/* Add Stop Button */}
        <button
          type="button"
          onClick={addStop}
          data-testid="add-stop-button"
          className="w-full py-3 border-2 border-dashed border-[rgba(20,184,166,0.3)] rounded-lg text-[#14B8A6] hover:border-[#14B8A6] hover:bg-[rgba(20,184,166,0.1)] transition-colors flex items-center justify-center gap-2 text-sm md:text-base"
        >
          <Plus className="w-4 h-4 md:w-5 md:h-5" />
          Add Stop
        </button>

        {/* Destination */}
        <div>
          <label className="block text-sm font-medium mb-2">
            <Navigation className="w-4 h-4 inline mr-1 text-[#8B5CF6]" />
            Destination
          </label>
          <LocationSearch
            selectedLocation={destination}
            onSelect={setDestination}
            placeholder="Search for destination location..."
            testId="destination"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium mb-2">Notes (Optional)</label>
          <textarea
            data-testid="trip-notes-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="glass-input w-full h-20 md:h-24 resize-none text-sm md:text-base"
            placeholder="Add any notes about this trip..."
          />
        </div>

        {/* Submit */}
        <button
          type="submit"
          data-testid="create-trip-submit"
          disabled={loading}
          className="btn-primary w-full text-sm md:text-base"
        >
          {loading ? 'Creating Trip...' : 'Create Trip'}
        </button>
      </form>
    </div>
  );
};

const LocationSearch = ({ selectedLocation, onSelect, placeholder, testId }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [savedLocations, setSavedLocations] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const searchTimeout = useRef(null);

  useEffect(() => {
    fetchSavedLocations();
  }, []);

  const fetchSavedLocations = async () => {
    try {
      const response = await api.get(`${API}/locations/saved`);
      setSavedLocations(response.data);
    } catch (error) {
      console.error('Error fetching saved locations:', error);
    }
  };

  const searchLocations = async (searchQuery) => {
    if (searchQuery.length < 3) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(`${API}/locations/search`, {
        query: searchQuery,
      });
      setResults(response.data);
    } catch (error) {
      console.error('Error searching locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (value) => {
    setQuery(value);
    setShowResults(true);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      searchLocations(value);
    }, 500);
  };

  const handleSelect = async (location) => {
    onSelect(location);
    setQuery(location.name);
    setShowResults(false);

    // Save location
    try {
      await api.post(`${API}/locations`, location);
    } catch (error) {
      console.error('Error saving location:', error);
    }
  };

  const displayResults = query.length >= 3 ? results : savedLocations.slice(0, 5);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          data-testid={`${testId}-search-input`}
          value={selectedLocation ? selectedLocation.name : query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setShowResults(true)}
          className="glass-input w-full pl-10"
          placeholder={placeholder}
          autoComplete="off"
        />
      </div>

      {showResults && (
        <div
          data-testid={`${testId}-results`}
          className="absolute z-10 w-full mt-2 glass-card max-h-64 overflow-y-auto"
        >
          {loading ? (
            <div className="p-4 text-center text-gray-400 text-sm">Searching...</div>
          ) : displayResults.length === 0 ? (
            <div className="p-4 text-center text-gray-400 text-sm">
              {query.length >= 3 ? 'No results found' : 'Start typing to search...'}
            </div>
          ) : (
            <div>
              {query.length < 3 && savedLocations.length > 0 && (
                <div className="p-2 text-xs text-gray-500 border-b border-[rgba(20,184,166,0.2)]">
                  Recent & Saved Locations
                </div>
              )}
              {displayResults.map((location, index) => (
                <button
                  key={index}
                  type="button"
                  data-testid={`${testId}-result-${index}`}
                  onClick={() => handleSelect(location)}
                  className="w-full p-3 text-left hover:bg-[rgba(20,184,166,0.1)] transition-colors border-b border-[rgba(20,184,166,0.1)] last:border-b-0"
                >
                  <div className="font-medium text-white text-sm md:text-base truncate">{location.name}</div>
                  <div className="text-xs md:text-sm text-gray-400 truncate">{location.address}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TripCreate;
